/**
 * @fileoverview Custom hook for per-institution approval status management
 * @description Provides functions and state to check user's approval status across all institutions,
 * enables read-only mode when no institutions are active
 */
import { useState, useCallback, useEffect } from "react";
import { fetchData, HTTP_STATUS, intl } from "dmsbasereact";

/**
 * Institution status values from backend (PersonInstitutionApproval.approval_status)
 */
export const InstitutionStatusEnum = {
  PENDING_DOCUMENT_UPLOAD: 2,
  PENDING_APPROVAL: 1,
  PENDING_TRAINING: 4,
  ACTIVE: 5,
  REJECTED: 6,
  PENDING_APPROVAL_GESTAO_LENA: 8,
  PENDING_APPROVAL_REGULATORIO: 7,
};

/**
 * Custom hook for per-institution approval status management
 * @param {boolean} autoFetch - Whether to fetch status on mount (default: true)
 * @returns {Object} Hook functions and state
 */
export const useInstitutionStatus = (autoFetch = true) => {
  const [loading, setLoading] = useState(false);
  const [institutionStatuses, setInstitutionStatuses] = useState([]);
  const [hasAtLeastOneActive, setHasAtLeastOneActive] = useState(false);
  const [hasAllInactive, setHasAllInactive] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);

  /**
   * Fetch the current user's approval status at all institutions
   * @returns {Promise<Array|null>} Array of institution statuses or null
   */
  const fetchInstitutionStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData("/account/my-institution-approvals", "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        const statuses = response.data?.institutions || [];

        setInstitutionStatuses(statuses);

        // CRITICAL FIX: Empty array means internal user (no institution restrictions)
        // For internal users, sidebar is controlled by role permissions, not institution status
        if (statuses.length === 0) {
          setHasAtLeastOneActive(true);   // No institution restrictions
          setHasAllInactive(false);
          setIsReadOnly(false);            // Not read-only, role permissions apply
        } else {
          // External users with institutions: check approval status
          const hasActive = statuses.some(s => s.is_approved);
          const allInactive = statuses.every(s => !s.is_approved);

          setHasAtLeastOneActive(hasActive);
          setHasAllInactive(allInactive);
          setIsReadOnly(allInactive);  // Read-only only if HAS institutions but none active
        }

        return statuses;
      }
      return null;
    } catch (error) {
      console.error("Error fetching institution statuses:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a formatted message explaining the read-only mode
   * @returns {string} Read-only mode message
   */
  const getReadOnlyMessage = useCallback(() => {
    if (!isReadOnly) return "";

    const pendingInstitutions = institutionStatuses.filter(s => s.is_pending);
    const rejectedInstitutions = institutionStatuses.filter(s => s.status === InstitutionStatusEnum.REJECTED);

    if (pendingInstitutions.length > 0) {
      const names = pendingInstitutions.map(i => i.institution_name).join(", ");
      return intl.get("Institution.PendingApproval.Message", { institutions: names })
        .d(`Your account is pending approval at: ${names}. The system is in read-only mode.`);
    }

    if (rejectedInstitutions.length > 0) {
      const names = rejectedInstitutions.map(i => i.institution_name).join(", ");
      return intl.get("Institution.Rejected.Message", { institutions: names })
        .d(`Your account was rejected at: ${names}. The system is in read-only mode.`);
    }

    return intl.get("Institution.NoActiveInstitutions.Message")
      .d("You are not active at any institution. The system is in read-only mode.");
  }, [isReadOnly, institutionStatuses]);

  /**
   * Get approval status for a specific institution
   * @param {number} institutionPk - Institution primary key
   * @returns {Object|null} Institution status object or null
   */
  const getInstitutionStatus = useCallback((institutionPk) => {
    return institutionStatuses.find(s => s.institution_pk === institutionPk) || null;
  }, [institutionStatuses]);

  /**
   * Check if user is approved at a specific institution
   * @param {number} institutionPk - Institution primary key
   * @returns {boolean} True if approved at this institution
   */
  const isApprovedAtInstitution = useCallback((institutionPk) => {
    const inst = getInstitutionStatus(institutionPk);
    return inst ? inst.is_approved : false;
  }, [getInstitutionStatus]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchInstitutionStatuses();
    }
  }, [autoFetch, fetchInstitutionStatuses]);

  return {
    loading,
    institutionStatuses,
    hasAtLeastOneActive,
    hasAllInactive,
    isReadOnly,
    getReadOnlyMessage,
    fetchInstitutionStatuses,
    getInstitutionStatus,
    isApprovedAtInstitution,
    // Backward compatibility (deprecated - returns first institution or defaults)
    institutionStatus: institutionStatuses[0]?.status || null,
    institutionName: institutionStatuses[0]?.institution_name || null,
  };
};

export default useInstitutionStatus;
